import {
  SurveyJson,
  StudyDesign,
  countStimulusCuts,
  DEFAULT_PLANNED_SAMPLE_SIZE,
  isMonadicDesign,
  maxRespondentsForDesign,
  resolveRetiredStudyDesign,
  targetRespondentsForDesign,
} from "@digital-twins/shared-types";
import type { StudyType } from "../types";
import { STUDY_CREATION_TYPE } from "../types";
import type { GoalIntakePayload } from "../components/goals/types";
import { getEffectiveRespondentLimits } from "../utils/respondentLimits";
import {
  perGroupFromRespondents,
  respondentsFromPerGroup,
} from "../utils/perGroupSizing";
import type { PopulationFilter } from "@/types/routes/test-results";
import type {
  AudienceFilterLabel,
  AudienceSelectionRef,
  SelectedSegment,
} from "@digital-twins/shared-types";
import type { CompanyProfile } from "@/lib/api";
import type { Population, Storage } from "@/types/routes/tests";

export type { Storage };

export interface CreationState {
  currentStep: number;
  loading: boolean;
  authReady: boolean;

  // Data loading
  populations: Population[];
  storages: Storage[];
  companyProfiles: CompanyProfile[];
  creditInfo: {
    available?: number;
    allowOverage?: boolean;
    overageRateCentsPerCredit?: number;
  } | null;
  audienceDataLoading: boolean;
  audienceDataLoaded: boolean;
  audienceLoadError: boolean;

  // Selections
  selectedStudyType: StudyType | null;
  selectedPopulationId: string;
  selectedStorageId: string;
  /**
   * The filter and saved segments picked with the population, until the
   * builder writes them to `draft.audience`, which owns them (C09-2): launch
   * and Review read the draft's. A resumed draft restores them from there.
   * Segment names are their labels.
   */
  populationFilter: PopulationFilter | null;
  filteredCount: number | null;
  /** Saved segments applied, by id with frozen conditions. */
  selectedSegments: Array<SelectedSegment> | null;
  /** The filter in the filter catalog's labels, from the same pick (C09-4). */
  populationFilterLabels: Array<AudienceFilterLabel> | null;

  editedSurveyJson: SurveyJson | null;
  /**
   * The draft revision `editedSurveyJson` was compiled at: the one Review
   * shows and prices. Launch sends it, and the server refuses any other.
   */
  reviewedRevision: number | null;
  /**
   * `draft.audience` at `reviewedRevision`: the audience Review shows, which
   * is the one launch reads. Null when the compile did not return its draft.
   */
  reviewedAudience: AudienceSelectionRef | null;
  /**
   * The design the draft's StudySpec resolved (`studySpec.resolved.design`,
   * plan S3.4 row 38): twin monadic exactly when two or more stimulus assets
   * are isolated. Null until a draft with a spec is loaded, which reads as the
   * product's default, comparative.
   */
  specDesign: StudyDesign | null;

  // Navigation
  returnToStep: number | null;

  // Form
  studyName: string;
  numRespondents: number | null;
  studyMode: 'quick' | 'full';
  /**
   * How twins meet the stimulus cuts. Only offered when the study has 2+ cuts.
   * Twin monadic is purchasable and billed as respondents × cuts (C4).
   */
  design: StudyDesign;
  /**
   * True once the researcher picked a design themselves. The questionnaire's
   * monadic recommendation only ever seeds an untouched default — re-editing
   * the questionnaire must not overwrite a deliberate comparative choice.
   */
  designExplicit: boolean;

  // Session restore
  questionnaireDraftId: string | null;

  // Content language for generated insights/responses (ISO code, default "en")
  contentLanguage: string;
  /**
   * True once the researcher picked a language themselves (chip, deep link,
   * or restored draft). Audience preselect only fills an untouched default —
   * re-selecting an audience must not overwrite a deliberate pick (D2c).
   */
  contentLanguageExplicit: boolean;

  // Deep-link initial message from home page
  initialMessage: string | null;

  // Goal-first flow (contract §0 of goal-first-execution-map.md)
  // Canonical goal id; null until the goal screen completes
  goalId: string | null;
  // Per-card intake values collected on the goal screen
  intake: GoalIntakePayload | null;
  // Delivery format flags (sent on launch; consumed by Plan B/D wiring)
  deckEnabled: boolean;
  twinChatEnabled: boolean;
}

export type CreationAction =
  | { type: "SET_AUTH_READY"; payload: boolean }
  | { type: "SET_LOADING"; payload: boolean }
  | { type: "SET_STEP"; payload: number }
  | { type: "FORCE_STEP"; payload: number }
  | {
      type: "SET_AUDIENCE_DATA";
      payload: {
        populations: Population[];
        storages: Storage[];
        companyProfiles: CompanyProfile[];
      };
    }
  | { type: "SET_AUDIENCE_LOADING"; payload: boolean }
  | { type: "SET_AUDIENCE_LOAD_ERROR"; payload: boolean }
  | { type: "RETRY_AUDIENCE_LOAD" }
  | { type: "SET_CREDIT_INFO"; payload: { available?: number } | null }
  | { type: "SELECT_STUDY_TYPE"; payload: StudyType }
  | { type: "SELECT_POPULATION"; payload: string }
  | {
      type: "SET_POPULATION_FILTER";
      payload: {
        filter: PopulationFilter | null;
        filteredCount: number | null;
        selectedSegments?: Array<SelectedSegment>;
        filterLabels?: Array<AudienceFilterLabel>;
      };
    }
  | { type: "SET_RETURN_TO_STEP"; payload: number | null }
  | {
      type: "SET_SURVEY_JSON";
      payload: SurveyJson | null;
      revision: number | null;
      /** The compiled draft's audience (`reviewedAudience`). */
      audience?: AudienceSelectionRef | null;
    }
  | { type: "SET_SPEC_DESIGN"; payload: StudyDesign | null }
  | { type: "SET_STUDY_NAME"; payload: string }
  | { type: "SET_NUM_RESPONDENTS"; payload: number | null }
  | { type: "SET_STUDY_MODE"; payload: 'quick' | 'full' }
  | { type: "SET_STUDY_DESIGN"; payload: StudyDesign }
  | { type: "SET_QUESTIONNAIRE_DRAFT_ID"; payload: string | null }
  | { type: "SET_INITIAL_MESSAGE"; payload: string | null }
  | { type: "SET_CONTENT_LANGUAGE"; payload: string }
  | { type: "SET_GOAL"; payload: string }
  | { type: "CLEAR_GOAL" }
  | { type: "SET_INTAKE"; payload: GoalIntakePayload | null }
  | { type: "SET_DECK_ENABLED"; payload: boolean }
  | { type: "SET_TWIN_CHAT_ENABLED"; payload: boolean }
  | { type: "RESET_TO_FRESH" };

export const CREATION_STORAGE_KEY = "study_creation_state";

export const CREATION_CONSTANTS = {
  DEFAULT_WORKERS: 50,
  SESSION_TTL_MS: 30 * 60 * 1000,
  DEFAULT_RESPONDENTS: DEFAULT_PLANNED_SAMPLE_SIZE,
  /** Data-mode (quick) hard ceiling on the review step respondent input. */
  QUICK_MODE_MAX_RESPONDENTS: 100,
} as const;

export const initialCreationState: CreationState = {
  currentStep: 1,
  loading: false,
  authReady: false,
  populations: [],
  storages: [],
  companyProfiles: [],
  creditInfo: null,
  audienceDataLoading: false,
  audienceDataLoaded: false,
  audienceLoadError: false,
  selectedStudyType: STUDY_CREATION_TYPE,
  selectedPopulationId: "",
  selectedStorageId: "",
  populationFilter: null,
  filteredCount: null,
  selectedSegments: null,
  populationFilterLabels: null,
  returnToStep: null,
  editedSurveyJson: null,
  reviewedRevision: null,
  reviewedAudience: null,
  specDesign: null,
  studyName: "",
  numRespondents: CREATION_CONSTANTS.DEFAULT_RESPONDENTS,
  contentLanguage: "en",
  contentLanguageExplicit: false,
  studyMode: 'full',
  design: "comparative",
  designExplicit: false,
  questionnaireDraftId: null,
  initialMessage: null,
  goalId: null,
  intake: null,
  deckEnabled: true,
  twinChatEnabled: true,
};

function canAdvanceToStep(target: number, state: CreationState): boolean {
  if (target <= state.currentStep) return true;
  if (target >= 3 && !state.editedSurveyJson) return false;
  return true;
}

function clampNumRespondentsValue(
  state: CreationState,
  value: number,
): number | null {
  const studyType = state.selectedStudyType ?? STUDY_CREATION_TYPE;
  const pop = state.populations.find((p) => p.id === state.selectedPopulationId);
  const limits = getEffectiveRespondentLimits(studyType, {
    filteredCount: state.filteredCount,
    personaCount: pop?.personaCount,
    designMax: maxRespondentsForDesign(state.design),
  });
  let max = limits.max;
  if (state.studyMode === "quick") {
    max = Math.min(max, CREATION_CONSTANTS.QUICK_MODE_MAX_RESPONDENTS);
  }
  let n = value;
  n = Math.min(n, max);
  n = Math.max(n, limits.min);
  return n;
}

/**
 * The design is resolved, never picked: it is the one the draft's StudySpec
 * resolved (plan S3.4 row 38), which Nest re-resolves on every commit from the
 * recorded exposure choice and the stimulus count. Comparison is the product
 * path, so a draft with no spec yet reads as comparative. Nothing here
 * re-derives it from the questionnaire's shape or the audience; a monadic
 * design only waits for the survey that carries its cuts, since groups are
 * sized per cut.
 */
function withResolvedDesign(state: CreationState): CreationState {
  if (state.designExplicit) {
    return state;
  }
  const spec: StudyDesign = state.specDesign ?? "comparative";
  const resolved: StudyDesign =
    isMonadicDesign(spec) && countStimulusCuts(state.editedSurveyJson) < 2
      ? "comparative"
      : spec;
  // The data-mode fallback that used to sit here existed for the retired
  // split-sample design, the only one that needed a group's worth of twins per
  // cut. Twin monadic reuses the same twins across cuts, so data mode's 100-twin
  // cap carries it at any cut count.
  if (resolved === state.design) {
    return state;
  }
  const next: CreationState = { ...state, design: resolved };
  return {
    ...next,
    numRespondents: respondentsOnDesignResolved(next, state, resolved),
  };
}

/**
 * Respondents when the design resolves under the marketer.
 *
 * The number they set is per group, so that is what carries across: the same
 * groups at the same size, which is also the same bill (`billableUnits` is
 * `perGroup × cuts` for both monadic designs). Carrying the total across
 * instead would silently resize the groups and move the price.
 */
function respondentsOnDesignResolved(
  next: CreationState,
  previous: CreationState,
  design: StudyDesign,
): number | null {
  const cutCount = countStimulusCuts(next.editedSurveyJson);
  // Only a design that already had groups carries a per-group number. Coming
  // from a single group the standing value is a whole-study default, not a
  // group size, so the 100-per-group target applies instead.
  const hadGroups = isMonadicDesign(previous.design) && cutCount >= 2;
  const perGroup = hadGroups
    ? perGroupFromRespondents(previous.design, cutCount, previous.numRespondents)
    : 0;
  if (perGroup <= 0) {
    return respondentsForDesignChange(next, design);
  }
  return clampNumRespondentsValue(
    next,
    respondentsFromPerGroup(design, cutCount, perGroup),
  );
}

/** Bump respondents up to the design's 100-per-cut target when below it. */
function respondentsForDesignChange(
  state: CreationState,
  design: StudyDesign,
): number | null {
  const cutCount = countStimulusCuts(state.editedSurveyJson);
  const target = targetRespondentsForDesign(design, cutCount);
  const current = state.numRespondents;
  const desired =
    target > 0 && (current == null || current < target) ? target : current;
  if (desired == null) {
    return null;
  }
  return clampNumRespondentsValue({ ...state, design }, desired);
}

export function creationReducer(
  state: CreationState,
  action: CreationAction,
): CreationState {
  switch (action.type) {
    case "SET_AUTH_READY":
      return { ...state, authReady: action.payload };
    case "SET_LOADING":
      return { ...state, loading: action.payload };
    case "SET_STEP": {
      if (!canAdvanceToStep(action.payload, state)) return state;
      const next: CreationState = { ...state, currentStep: action.payload };
      if (action.payload !== 3 || next.numRespondents == null) {
        return next;
      }
      return {
        ...next,
        numRespondents: clampNumRespondentsValue(next, next.numRespondents),
      };
    }
    case "FORCE_STEP": {
      // Bypasses canAdvanceToStep — use only for explicit user intent (e.g. duplicate modal).
      const next: CreationState = { ...state, currentStep: action.payload };
      if (action.payload !== 3 || next.numRespondents == null) {
        return next;
      }
      return {
        ...next,
        numRespondents: clampNumRespondentsValue(next, next.numRespondents),
      };
    }
    case "SET_AUDIENCE_DATA": {
      // Persona counts arrive with this payload, so the design resolves here
      // for a session restored before the audiences loaded.
      const next = withResolvedDesign({
        ...state,
        populations: action.payload.populations,
        storages: action.payload.storages,
        companyProfiles: action.payload.companyProfiles,
        audienceDataLoaded: true,
        audienceDataLoading: false,
        audienceLoadError: false,
      });
      if (next.numRespondents == null) {
        return next;
      }
      return {
        ...next,
        numRespondents: clampNumRespondentsValue(next, next.numRespondents),
      };
    }
    case "SET_AUDIENCE_LOADING":
      return { ...state, audienceDataLoading: action.payload };
    case "SET_AUDIENCE_LOAD_ERROR":
      return {
        ...state,
        audienceDataLoading: false,
        audienceDataLoaded: true,
        audienceLoadError: action.payload,
      };
    case "RETRY_AUDIENCE_LOAD":
      return {
        ...state,
        audienceDataLoaded: false,
        audienceLoadError: false,
        audienceDataLoading: false,
      };
    case "SET_CREDIT_INFO":
      return { ...state, creditInfo: action.payload };
    case "SELECT_STUDY_TYPE":
      return { ...state, selectedStudyType: action.payload };
    case "SELECT_POPULATION": {
      // RND-2668: preselect the study language from the chosen audience when
      // the researcher has not picked one themselves. An explicit chip /
      // deep-link / restored-draft pick always wins (D2c).
      const selected = state.populations.find((p) => p.id === action.payload);
      const audienceLanguage = selected?.language?.trim();
      const contentLanguage = state.contentLanguageExplicit
        ? state.contentLanguage
        : audienceLanguage || state.contentLanguage;
      // A filter and segments belong to the population they were picked on:
      // another population clears them (C09-2).
      const narrowing =
        action.payload === state.selectedPopulationId
          ? {}
          : {
              populationFilter: null,
              filteredCount: null,
              selectedSegments: null,
              populationFilterLabels: null,
            };
      // The pool decides whether each group can have its own twins.
      return withResolvedDesign({
        ...state,
        ...narrowing,
        selectedPopulationId: action.payload,
        contentLanguage,
      });
    }
    case "SET_POPULATION_FILTER": {
      // Filtering shrinks the pool, which can move the study off its own twins
      // per group and onto shared ones.
      const next = withResolvedDesign({
        ...state,
        populationFilter: action.payload.filter,
        filteredCount: action.payload.filteredCount,
        selectedSegments: action.payload.selectedSegments ?? null,
        // Labels belong to this filter: none without one.
        populationFilterLabels: action.payload.filter
          ? (action.payload.filterLabels ?? null)
          : null,
      });
      if (next.numRespondents == null) {
        return next;
      }
      return {
        ...next,
        numRespondents: clampNumRespondentsValue(next, next.numRespondents),
      };
    }
    case "SET_RETURN_TO_STEP":
      return { ...state, returnToStep: action.payload };
    case "SET_SPEC_DESIGN":
      // A new resolved design (a hydrated or re-committed draft) must reprice,
      // so clear designExplicit and re-resolve. A no-op notify of the same
      // design must not — returning the same reference lets React bail.
      if (state.specDesign === action.payload) {
        return state;
      }
      return withResolvedDesign({
        ...state,
        specDesign: action.payload,
        designExplicit: false,
      });
    case "SET_SURVEY_JSON": {
      // A monadic design is meaningless below two cuts; editing the stimulus
      // down must not leave a stale, unlaunchable selection behind.
      const cutCount = countStimulusCuts(action.payload);
      return withResolvedDesign({
        ...state,
        editedSurveyJson: action.payload,
        reviewedRevision: action.revision,
        reviewedAudience: action.audience ?? null,
        design: cutCount >= 2 ? state.design : "comparative",
      });
    }
    case "SET_STUDY_NAME":
      return { ...state, studyName: action.payload };
    case "SET_NUM_RESPONDENTS":
      return { ...state, numRespondents: action.payload };
    case "SET_STUDY_MODE": {
      // Data mode caps at 100 respondents, which every surviving design can
      // carry: twin monadic reads the same 100 twins once per cut. The
      // fall-to-twin-monadic branch that used to live here belonged to the
      // retired split-sample design (TRUE_MONADIC_RETIRED).
      const next: CreationState = { ...state, studyMode: action.payload };
      if (next.numRespondents == null) {
        return next;
      }
      return {
        ...next,
        numRespondents: clampNumRespondentsValue(next, next.numRespondents),
      };
    }
    case "SET_STUDY_DESIGN": {
      // Raise respondents to the design's per-cut target when below it.
      // TRUE_MONADIC_RETIRED: an explicit pick of the retired design is
      // downgraded here, the same way the backend evaluator downgrades it, so
      // the state can never hold a design no launch would run.
      const design = resolveRetiredStudyDesign(action.payload);
      const next: CreationState = {
        ...state,
        design,
        designExplicit: true,
      };
      return {
        ...next,
        numRespondents: respondentsForDesignChange(next, design),
      };
    }
    case "SET_QUESTIONNAIRE_DRAFT_ID":
      return { ...state, questionnaireDraftId: action.payload };
    case "SET_INITIAL_MESSAGE":
      return { ...state, initialMessage: action.payload };
    case "SET_CONTENT_LANGUAGE":
      return {
        ...state,
        contentLanguage: action.payload,
        contentLanguageExplicit: true,
      };
    case "SET_GOAL":
      return { ...state, goalId: action.payload };
    case "CLEAR_GOAL":
      return { ...state, goalId: null, intake: null, currentStep: 1 };
    case "SET_INTAKE":
      return { ...state, intake: action.payload };
    case "SET_DECK_ENABLED":
      return { ...state, deckEnabled: action.payload };
    case "SET_TWIN_CHAT_ENABLED":
      return { ...state, twinChatEnabled: action.payload };
    case "RESET_TO_FRESH":
      // Same-route back from `/tests/new?draftId=` keeps this reducer alive.
      // Drop draft progress so the goal screen cannot paint over the builder.
      return {
        ...initialCreationState,
        authReady: state.authReady,
        populations: state.populations,
        storages: state.storages,
        companyProfiles: state.companyProfiles,
        creditInfo: state.creditInfo,
        audienceDataLoading: state.audienceDataLoading,
        audienceDataLoaded: state.audienceDataLoaded,
        audienceLoadError: state.audienceLoadError,
        selectedStudyType: state.selectedStudyType,
      };
    default:
      return state;
  }
}
